import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { captureEvent } from "@/lib/analytics";
import { PageHeader, Card, EmptyState, Spinner } from "@/components/ui";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { nextStatus } from "@/components/claims/claimStatus";
import { NewClaimForm } from "@/components/claims/NewClaimForm";
import { ClaimCard } from "@/components/claims/ClaimCard";
import { AppealModal } from "@/components/claims/AppealModal";
import { LetterModal } from "@/components/claims/LetterModal";

export default function Claims() {
  const [items, setItems] = useState(null);
  const [appealFor, setAppealFor] = useState(null);
  const [letterView, setLetterView] = useState(null);

  const load = useCallback(async () => setItems(await api.listEobs()), []);
  useEffect(() => { load(); }, [load]);

  const createClaim = async (payload) => { await api.createEob(payload); captureEvent("claim_uploaded", { method: "manual" }); await load(); toast.success("Claim analyzed"); };
  const uploadClaim = async (payload) => { await api.uploadEob(payload); captureEvent("claim_uploaded", { method: "upload" }); await load(); toast.success("Bill analyzed"); };

  const advance = async (eob) => {
    const n = nextStatus(eob.status);
    if (!n) return;
    await api.updateEobStatus(eob.id, n);
    await load();
    if (n === "resolved") window.dispatchEvent(new Event("kb:check-feedback"));
  };

  const openAttachment = async (s3_key) => {
    try { const r = await api.blobDownloadUrl(s3_key); if (r?.url) window.open(r.url, "_blank"); }
    catch { toast.error("Could not open attachment"); }
  };

  if (!items) return <Spinner />;

  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="AI" title="Claims & appeals" />
      <p className="text-sm text-subtle -mt-3 mb-5 max-w-2xl">
        Upload an EOB or bill. Gemini extracts the fields and flags upcoding, duplicates, and No Surprises Act issues.
        Claude Sonnet drafts an ERISA-aware appeal for your review — nothing is auto-submitted.
      </p>

      <NewClaimForm onUpload={uploadClaim} onCreate={createClaim} />

      <h2 className="font-display text-xl text-ink mt-8 mb-3">Case tracker</h2>
      {items.length === 0 ? (
        <Card><EmptyState testid="eob-empty" icon={FileText} title="No claims audited yet"
          body="Upload an EOB or bill — Gemini reads it, flags overcharges, and Claude drafts your appeal." /></Card>
      ) : (
        <div className="space-y-4">
          {items.map((c) => (
            <ClaimCard key={c.id} c={c}
              onDraft={() => setAppealFor(c)}
              onView={() => setLetterView(c)}
              onAdvance={() => advance(c)}
              onOpenAttachment={openAttachment} />
          ))}
        </div>
      )}

      <AppealModal eob={appealFor} onClose={() => setAppealFor(null)} onDrafted={async () => { setAppealFor(null); await load(); toast.success("Appeal drafted"); }} />
      <LetterModal eob={letterView} onClose={() => setLetterView(null)} />
    </div>
  );
}
